import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { type ActionRecord } from "@/shared/domain/actions";
import { useAction, useActions } from "@/shared/domain/actions/hooks";
import { TriggerSourceType, variableRoots } from "@/shared/domain/triggers";
import { Combobox, Field } from "@/shared/foundation/primitives";

import { computeDefaultsOf } from "../../invocationSpec/actionDefaults";
import { FormBlock } from "../../invocationSpec/FormBlock";
import { type TriggeringSubject } from "../../invocationSpec/inputSourceCopy";
import { InvocationSpecFields } from "../../invocationSpec/InvocationSpecFields";
import { suppliedInputFor } from "../formScope";
import { type TriggerFormValues } from "../triggerFormSchema";
import { useFormTemplateCatalog } from "../useFormTemplateCatalog";

import { InputDataHeader } from "./InputDataHeader";
import styles from "./targets.module.css";

export interface InvokeActionTargetCardProps {
  index: number;
}

/** Which firing the dataset row is worded for. */
function subjectOfFiring(
  supplied: ReturnType<typeof suppliedInputFor>,
): TriggeringSubject {
  return supplied === "changed-file" ? "file" : "dataset";
}

/** The action a target names, as the form holds it. */
type ActionReferenceValues = {
  name: string;
  owner: string | null;
  digest: string | null;
} | null;

/** The action to invoke, and everything about how to run it. */
export function InvokeActionTargetCard({ index }: InvokeActionTargetCardProps) {
  const { control, setValue } = useFormContext<TriggerFormValues>();
  const firesOn = useWatch<TriggerFormValues, "firesOn">({
    control,
    name: "firesOn",
  });
  const catalog = useFormTemplateCatalog();
  const actionTitleId = React.useId();
  const orgActions = useActions("org");
  const hubActions = useActions("actionhub");

  const reference = useWatch<TriggerFormValues>({
    control,
    name: `targets.${index}.spec.action`,
  }) as ActionReferenceValues;

  const offered = [...(orgActions.data ?? []), ...(hubActions.data ?? [])];
  // The stored action may be one this org cannot list (a hub action pinned by
  // digest), so it is fetched by name rather than found in the offered list.
  const storedAction = useAction(
    reference?.name,
    reference?.digest ?? undefined,
    { resourceOwnerId: reference?.owner ?? undefined },
  );
  const chosen =
    offered.find(
      (action) =>
        action.name === reference?.name &&
        (reference.owner === null || action.org_id === reference.owner),
    ) ??
    storedAction.data ??
    null;
  const supplied = suppliedInputFor(firesOn);
  // Whether the firing puts a dataset in the namespace, which is what the
  // placeholder resolves against: an unresolved one fails the dispatch, so
  // this is read off the catalog rather than assumed from the entity.
  const carriesADataset =
    firesOn.type === TriggerSourceType.Events &&
    variableRoots(firesOn.events).includes("dataset");
  // A file firing's subject is the file, so the row names the dataset as that
  // file's rather than as one the trigger fired on.
  const triggeringSubject: TriggeringSubject | undefined = carriesADataset
    ? subjectOfFiring(supplied)
    : undefined;

  return (
    <div className={styles.body} data-testid={`trigger-target-${index}-action`}>
      <FormBlock
        titleId={actionTitleId}
        title="Action"
        hint="The action to run when this trigger fires."
      >
        <Controller
          control={control}
          name={`targets.${index}.spec.action`}
          render={({ field, fieldState }) => (
            <Field.Root invalid={fieldState.invalid}>
              <Combobox<ActionRecord>
                items={offered}
                value={chosen}
                onValueChange={(action) => {
                  field.onChange(
                    action === null
                      ? null
                      : {
                          name: action.name,
                          owner: action.org_id,
                          // A newly chosen action invokes its latest version; a
                          // stored digest pins the one it replaced.
                          digest: null,
                        },
                  );
                  // Which pairs of vCPU and memory are allowed is the action's
                  // own business, so the pair follows the action rather than
                  // outliving it. Everything else Advanced settings holds is a
                  // placeholder, and needs no such handover.
                  const compute =
                    action === null ? undefined : computeDefaultsOf(action);
                  setValue(
                    `targets.${index}.spec.overrides.cpu`,
                    compute?.cpu,
                    { shouldDirty: true },
                  );
                  setValue(
                    `targets.${index}.spec.overrides.memory`,
                    compute?.memory,
                    { shouldDirty: true },
                  );
                }}
                itemToStringLabel={(action) => action.name}
                isItemEqualToValue={(item, value) =>
                  item.name === value.name && item.org_id === value.org_id
                }
                autoHighlight
                size="lg"
              >
                <Combobox.TriggerInput
                  placeholder="Search actions"
                  aria-labelledby={actionTitleId}
                  ref={field.ref}
                />
                <Combobox.Content>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(action: ActionRecord) => (
                      <Combobox.Item
                        key={`${action.org_id}/${action.name}`}
                        value={action}
                      />
                    )}
                  </Combobox.List>
                </Combobox.Content>
              </Combobox>
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          )}
        />
      </FormBlock>

      {chosen !== null && (
        <InvocationSpecFields
          name={`targets.${index}.spec`}
          action={chosen}
          templateCatalog={catalog}
          inputDataHeader={
            supplied === null ? undefined : (
              <InputDataHeader index={index} supplied={supplied} />
            )
          }
          hasSourceDataset={firesOn.type !== TriggerSourceType.Schedule}
          triggeringSubject={triggeringSubject}
        />
      )}
    </div>
  );
}
